<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-human-pregnant-erect me-2"></i>{{ $t('page.fp', 'Family Planning Register') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.active || 0 }}</div><div class="hms-stat-lbl">Active Clients</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.new_30d || 0 }}</div><div class="hms-stat-lbl">New (30d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.overdue || 0 }}</div><div class="hms-stat-lbl">Appointment overdue</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ defaulterStats.lost || 0 }}</div><div class="hms-stat-lbl">Lost (&gt;90d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.exited || 0 }}</div><div class="hms-stat-lbl">Exited / Pregnant</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='clients' }" @click="tab='clients'"><i class="mdi mdi-account-multiple me-1"></i>Active Clients</button>
        <button class="hms-tab" :class="{ active: tab==='detail' }" @click="tab='detail'" :disabled="!detail.client"><i class="mdi mdi-card-account-details me-1"></i>Client Card</button>
        <button class="hms-tab" :class="{ active: tab==='defaulters' }" @click="tab='defaulters'; loadDefaulters()"><i class="mdi mdi-alert me-1"></i>Defaulters</button>
        <button class="hms-tab" :class="{ active: tab==='indicators' }" @click="tab='indicators'; loadIndicators()"><i class="mdi mdi-chart-pie me-1"></i>Method Mix &amp; CYP</button>
        <button class="hms-tab" :class="{ active: tab==='methods' }" @click="tab='methods'"><i class="mdi mdi-list-box me-1"></i>Method Catalog</button>
      </div>

      <!-- ============ CLIENTS ============ -->
      <div v-if="tab==='clients'">
        <div class="d-flex justify-content-between mb-2 flex-wrap gap-2">
          <div class="d-flex gap-2">
            <input v-model="search" class="hms-input" placeholder="Name / FP # / phone" @keyup.enter="loadClients" style="min-width:240px" />
            <select v-model="statusFilter" class="hms-input" @change="loadClients">
              <option value="active">Active</option>
              <option value="defaulter">Defaulter</option>
              <option value="exited">Exited</option>
              <option value="desire_pregnancy">Desire pregnancy</option>
              <option value="completed_family">Completed family size</option>
              <option value="">All</option>
            </select>
            <select v-model="methodFilter" class="hms-input" @change="loadClients">
              <option value="">All methods</option>
              <option v-for="m in methods" :key="m.method_id" :value="m.method_id">{{ m.method_code }}</option>
            </select>
          </div>
          <button class="hms-btn hms-btn-success" @click="openEnrolModal()"><i class="mdi mdi-account-plus"></i> Enrol</button>
        </div>
        <table class="hms-table">
          <thead>
            <tr><th>FP #</th><th>Client</th><th>Method</th><th>Started</th><th>Next visit</th><th>G/P</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="c in clients" :key="c.fp_id" :class="rowClass(c)">
              <td><strong>{{ c.fp_number }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }} · {{ c.phone || '' }}</div></td>
              <td>
                <span v-if="c.method_code"><strong>{{ c.method_code }}</strong> <span class="hms-pill" :class="catPill(c.method_category)">{{ c.method_category }}</span></span>
                <span v-else class="hms-muted-sm">none</span>
              </td>
              <td>{{ c.method_started_date || '—' }}</td>
              <td :class="c.days_to_appointment != null && c.days_to_appointment < 0 ? 'st-bad' : ''">
                {{ c.next_visit_date || '—' }}
                <div v-if="c.days_to_appointment != null" class="hms-muted-sm">
                  {{ c.days_to_appointment >= 0 ? 'in ' + c.days_to_appointment + 'd' : Math.abs(c.days_to_appointment) + 'd late' }}
                </div>
              </td>
              <td>{{ c.gravida || '?' }}/{{ c.para || '?' }}</td>
              <td><span class="hms-pill" :class="statusPill(c.status)">{{ c.status }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(c)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ DETAIL ============ -->
      <div v-if="tab==='detail' && detail.client">
        <div class="hms-card mb-3">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.client.patient_name }}</div>
                <div class="hms-muted-sm">{{ detail.client.fp_number }} · {{ detail.client.patient_no }} · {{ detail.client.sex }} · DOB {{ detail.client.date_of_birth }}</div>
                <div class="hms-muted-sm" v-if="detail.client.clinical_history"><i class="mdi mdi-alert-circle text-warning"></i> {{ detail.client.clinical_history }}</div>
              </div>
              <div class="text-end">
                <div><strong>{{ detail.client.method_code || '—' }}</strong> <span v-if="detail.client.method_category" class="hms-pill" :class="catPill(detail.client.method_category)">{{ detail.client.method_category }}</span></div>
                <div class="hms-muted-sm">Started {{ detail.client.method_started_date || '—' }}</div>
                <div class="hms-muted-sm">Next visit: <strong>{{ detail.client.next_visit_date || '—' }}</strong></div>
                <div class="hms-muted-sm">G/P: {{ detail.client.gravida || '?' }}/{{ detail.client.para || '?' }} · Living: {{ detail.client.living_children || '?' }}</div>
              </div>
            </div>
            <div class="d-flex gap-2 mt-3">
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openVisitModal('refill')"><i class="mdi mdi-clipboard-text"></i> Record Visit / Refill</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm" @click="openSwitchModal()"><i class="mdi mdi-swap-horizontal"></i> Switch Method</button>
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openVisitModal('removal')"><i class="mdi mdi-close-octagon"></i> Removal / Discontinue</button>
            </div>
          </div>
        </div>

        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">Visit history</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.visits.length" class="hms-table">
                <thead><tr><th>Date</th><th>Type</th><th>Method</th><th>Qty</th><th>BP / Wt</th><th>CYP</th><th>By</th></tr></thead>
                <tbody>
                  <tr v-for="v in detail.visits" :key="v.visit_id">
                    <td>{{ v.visit_date }}</td>
                    <td><span class="hms-pill" :class="visitTypePill(v.visit_type)">{{ v.visit_type }}</span></td>
                    <td>{{ v.method_code || '—' }}</td>
                    <td>{{ v.quantity_dispensed || '—' }}</td>
                    <td>{{ v.bp_systolic || '—' }}/{{ v.bp_diastolic || '—' }} · {{ v.weight_kg || '—' }}</td>
                    <td>{{ v.cyp_earned || 0 }}</td>
                    <td>{{ v.recorded_by_name || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No visits yet.</div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Method changes</div>
            <div class="hms-card-body p-0">
              <table v-if="detail.methodChanges.length" class="hms-table">
                <thead><tr><th>Date</th><th>From</th><th>To</th><th>Reason</th></tr></thead>
                <tbody>
                  <tr v-for="c in detail.methodChanges" :key="c.change_id">
                    <td>{{ c.changed_at }}</td>
                    <td>{{ c.from_code || '—' }}</td>
                    <td>{{ c.to_code || 'discontinued' }}</td>
                    <td>{{ formatReason(c.reason) }}<div v-if="c.notes" class="hms-muted-sm">{{ c.notes }}</div></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No switches.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ DEFAULTERS ============ -->
      <div v-if="tab==='defaulters'">
        <div v-if="!defaulters.length" class="hms-empty"><i class="mdi mdi-check-circle"></i><h5>No defaulters</h5></div>
        <table v-else class="hms-table">
          <thead><tr><th>FP #</th><th>Client</th><th>Method</th><th>Last appt</th><th>Days late</th><th>Phone</th><th>CHV</th><th></th></tr></thead>
          <tbody>
            <tr v-for="d in defaulters" :key="d.fp_id" :class="d.days_late > 90 ? 'row-action' : (d.days_late >= 30 ? 'row-warn' : '')">
              <td><strong>{{ d.fp_number }}</strong></td>
              <td>{{ d.patient_name }}</td>
              <td>{{ d.method_code }}</td>
              <td>{{ d.next_visit_date }}</td>
              <td><strong :class="d.days_late > 30 ? 'st-bad' : ''">{{ d.days_late }}d</strong></td>
              <td>{{ d.phone || '—' }}</td>
              <td>{{ d.chv_name || '—' }} <span class="hms-muted-sm">{{ d.chv_phone }}</span></td>
              <td><button class="hms-btn-link" @click="openDetailById(d.fp_id)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ INDICATORS ============ -->
      <div v-if="tab==='indicators'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="indicatorPeriod" class="hms-input" @change="loadIndicators" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-grid-2 mb-3">
          <div class="hms-card">
            <div class="hms-card-head">Service volume</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Active clients</span><strong>{{ indicators.Indicators && indicators.Indicators.active_clients || 0 }}</strong></div>
              <div class="hms-ind-row"><span>New acceptors (period)</span><strong>{{ indicators.Indicators && indicators.Indicators.new_acceptors || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Revisits (period)</span><strong>{{ indicators.Indicators && indicators.Indicators.revisits || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Postnatal FP uptake</span><strong>{{ indicators.Indicators && indicators.Indicators.postnatal_uptake || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Switches due to side-effects</span><strong>{{ indicators.Indicators && indicators.Indicators.changes_side_effects || 0 }}</strong></div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Couple-Years of Protection (CYP)</div>
            <div class="hms-card-body">
              <div class="hms-cyp-banner">
                <div class="hms-cyp-num">{{ Number(indicators.Indicators && indicators.Indicators.cyp_total || 0).toFixed(1) }}</div>
                <div class="hms-muted-sm">CYP earned this {{ formatPeriod(indicatorPeriod) }}</div>
              </div>
              <p class="hms-muted-sm mt-2">CYP = couple-years of contraceptive protection provided. UNFPA / Track20 standard. Based on units dispensed × method CYP factor.</p>
            </div>
          </div>
        </div>
        <div class="hms-card">
          <div class="hms-card-head">Method mix — active clients</div>
          <div class="hms-card-body">
            <div v-for="m in indicators.MethodMix || []" :key="m.method_code" class="hms-mix-row">
              <div class="hms-mix-label">
                <strong>{{ m.method_code }}</strong>
                <span class="hms-muted-sm">{{ m.method_name }}</span>
                <span class="hms-pill" :class="catPill(m.method_category)">{{ m.method_category }}</span>
              </div>
              <div class="hms-mix-bar"><div class="hms-mix-fill" :style="{ width: mixPct(m.clients) + '%', background: catColor(m.method_category) }"></div></div>
              <div class="hms-mix-num"><strong>{{ m.clients }}</strong><div class="hms-muted-sm">{{ mixPct(m.clients) }}%</div></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ METHOD CATALOG ============ -->
      <div v-if="tab==='methods'">
        <table class="hms-table">
          <thead><tr><th>Code</th><th>Method</th><th>Category</th><th>LARC?</th><th>Permanent?</th><th>Duration</th><th>CYP factor</th><th>Notes</th></tr></thead>
          <tbody>
            <tr v-for="m in methods" :key="m.method_id">
              <td><strong>{{ m.method_code }}</strong></td>
              <td>{{ m.method_name }}</td>
              <td><span class="hms-pill" :class="catPill(m.method_category)">{{ m.method_category }}</span></td>
              <td>{{ m.is_long_acting == 1 ? 'Yes' : 'No' }}</td>
              <td>{{ m.is_permanent == 1 ? 'Yes' : 'No' }}</td>
              <td>{{ m.duration_months ? m.duration_months + ' mo' : '—' }}</td>
              <td>{{ m.cyp_factor }} <span class="hms-muted-sm">{{ m.cyp_unit }}</span></td>
              <td class="hms-muted-sm">{{ m.notes }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===== ENROL MODAL ===== -->
    <div v-if="enrolModal" class="hms-modal-bg" @click.self="enrolModal=false">
      <div class="hms-modal" style="width:760px">
        <div class="hms-modal-head"><h5>Enrol on Family Planning</h5><button class="hms-icon-btn" @click="enrolModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!enrol.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="enrol.patient_search" class="hms-input" placeholder="Name / patient #" @keyup.enter="findEnrolPatient" />
              <button class="hms-btn hms-btn-blue" @click="findEnrolPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ enrol.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="enrol.patient_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-3"><label class="hms-label">Reg. date *</label><input v-model="enrol.registration_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Age</label><input v-model.number="enrol.age_at_registration" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Gravida</label><input v-model.number="enrol.gravida" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Para</label><input v-model.number="enrol.para" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Living children</label><input v-model.number="enrol.living_children" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Marital status</label>
              <select v-model="enrol.marital_status" class="hms-input">
                <option value="">—</option><option value="single">Single</option><option value="married">Married</option>
                <option value="separated">Separated</option><option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option><option value="cohabiting">Cohabiting</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Education</label>
              <select v-model="enrol.education_level" class="hms-input">
                <option value="">—</option><option value="none">None</option><option value="primary">Primary</option>
                <option value="secondary">Secondary</option><option value="tertiary">Tertiary</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Referral</label>
              <select v-model="enrol.referral_source" class="hms-input">
                <option value="OPD">OPD</option><option value="ANC">ANC</option><option value="postnatal">Postnatal</option>
                <option value="postabortion">Post-abortion</option><option value="community">Community / CHV</option>
                <option value="self">Self / walk-in</option><option value="partner">Partner</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">LMP</label><input v-model="enrol.lmp_date" type="date" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="enrol.breastfeeding" :true-value="1" :false-value="0" /> Breastfeeding</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="enrol.partner_aware" :true-value="1" :false-value="0" /> Partner aware</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="enrol.screening_complete" :true-value="1" :false-value="0" /> WHO MEC screening done</label></div>
            <div class="col-12"><label class="hms-label">Clinical history (HTN/DM/migraine/cancer/SCD/liver…)</label><textarea v-model="enrol.clinical_history" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Method *</label>
              <select v-model.number="enrol.current_method_id" class="hms-input">
                <option :value="null">— select —</option>
                <option v-for="m in methods" :key="m.method_id" :value="m.method_id">{{ m.method_code }} — {{ m.method_name }}</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Started</label><input v-model="enrol.method_started_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Next visit</label><input v-model="enrol.next_visit_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV name</label><input v-model="enrol.chv_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV phone</label><input v-model="enrol.chv_phone" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="enrol.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="enrolModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !enrol.patient_id" @click="saveEnrol">{{ saving?'Saving…':'Enrol' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== VISIT MODAL ===== -->
    <div v-if="visitModal" class="hms-modal-bg" @click.self="visitModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>{{ visit.visit_type === 'removal' ? 'Removal / Discontinuation' : 'Visit / Refill' }}</h5><button class="hms-icon-btn" @click="visitModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Date *</label><input v-model="visit.visit_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Type</label>
              <select v-model="visit.visit_type" class="hms-input">
                <option value="initiation">Initiation</option><option value="refill">Refill</option>
                <option value="switch">Switch</option><option value="review">Review</option>
                <option value="side_effect">Side-effect</option><option value="removal">Removal</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Method given</label>
              <select v-model.number="visit.method_id" class="hms-input">
                <option :value="null">— none —</option>
                <option v-for="m in methods" :key="m.method_id" :value="m.method_id">{{ m.method_code }}</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Quantity</label><input v-model.number="visit.quantity_dispensed" type="number" step="0.01" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Days supply</label><input v-model.number="visit.days_supply" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BP Sys</label><input v-model.number="visit.bp_systolic" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">BP Dia</label><input v-model.number="visit.bp_diastolic" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Wt (kg)</label><input v-model.number="visit.weight_kg" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Hb (g/dL)</label><input v-model.number="visit.hb_level" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Satisfaction (1-5)</label><input v-model.number="visit.satisfaction_score" type="number" min="1" max="5" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Next visit</label><input v-model="visit.next_visit_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Side effects / complaints</label><textarea v-model="visit.side_effects" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="visit.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="visitModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveVisit">{{ saving?'Saving…':'Save Visit' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== SWITCH MODAL ===== -->
    <div v-if="switchModal" class="hms-modal-bg" @click.self="switchModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Switch / Discontinue Method</h5><button class="hms-icon-btn" @click="switchModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">Currently on: <strong>{{ detail.client && detail.client.method_code || '—' }}</strong></div>
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">New method (or leave blank to discontinue)</label>
              <select v-model.number="switchForm.to_method_id" class="hms-input">
                <option :value="null">— discontinue —</option>
                <option v-for="m in methods" :key="m.method_id" :value="m.method_id">{{ m.method_code }} — {{ m.method_name }}</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Reason</label>
              <select v-model="switchForm.reason" class="hms-input">
                <option value="side_effects">Side effects</option>
                <option value="desire_pregnancy">Desire pregnancy</option>
                <option value="medical_contraindication">Medical contraindication</option>
                <option value="partner_objection">Partner objection</option>
                <option value="method_failure">Method failure</option>
                <option value="expired">Method expired</option>
                <option value="simplification">Switch to simpler method</option>
                <option value="transferred">Transferred out</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Date</label><input v-model="switchForm.changed_at" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="switchForm.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="switchModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveSwitch">{{ saving?'Saving…':'Confirm' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "FamilyPlanning",
  components: { Navigation },
  data() {
    return {
      tab: 'clients', saving: false,
      stats: {}, defaulterStats: {},
      methods: [],

      search: '', statusFilter: 'active', methodFilter: '',
      clients: [],

      detail: { client: null, visits: [], methodChanges: [] },

      defaulters: [],

      indicatorPeriod: 'this_month',
      indicators: {},

      enrolModal: false, enrol: this.blankEnrol(),
      visitModal: false, visit: this.blankVisit(),
      switchModal: false, switchForm: { to_method_id: null, reason: 'side_effects', changed_at: '', notes: '' }
    };
  },
  computed: {
    totalActiveClients() { return (this.indicators.MethodMix || []).reduce((s, m) => s + parseInt(m.clients || 0), 0); }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankEnrol() {
      const today = new Date().toISOString().substring(0,10);
      return {
        patient_id: null, patient_name: '', patient_search: '',
        registration_date: today, age_at_registration: null,
        marital_status: '', education_level: '', occupation: '',
        gravida: null, para: null, living_children: null,
        lmp_date: '', breastfeeding: 0, partner_aware: 1, screening_complete: 1,
        clinical_history: '',
        current_method_id: null, method_started_date: today, next_visit_date: '',
        referral_source: 'OPD', chv_name: '', chv_phone: '', notes: ''
      };
    },
    blankVisit() {
      return {
        visit_date: new Date().toISOString().substring(0,10),
        visit_type: 'refill', method_id: null,
        quantity_dispensed: null, days_supply: null,
        bp_systolic: null, bp_diastolic: null, weight_kg: null, hb_level: null,
        side_effects: '', satisfaction_score: null,
        next_visit_date: '', notes: ''
      };
    },

    async loadAll() {
      await Promise.all([this.loadMethods(), this.loadClients(), this.loadDefaulters()]);
    },
    async loadMethods() {
      try {
        const r = await executeJson({ function: 'getFpMethods' }, hms);
        if (r.data && r.data.success) this.methods = (r.data.data && r.data.data.Methods) || [];
      } catch (e) { console.error(e); }
    },
    async loadClients() {
      try {
        const r = await executeJson({ function: 'getFpClients', search: this.search, status: this.statusFilter, method_id: this.methodFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.clients = d.Clients || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openEnrolModal() { this.enrol = this.blankEnrol(); this.enrolModal = true; },
    async findEnrolPatient() {
      const q = (this.enrol.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.enrol.patient_id = p.patient_id;
        this.enrol.patient_name = p.first_name + ' ' + p.last_name;
        if (p.dob) {
          const years = Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25 * 86400000));
          if (!this.enrol.age_at_registration) this.enrol.age_at_registration = years;
        }
      }
    },
    async saveEnrol() {
      this.saving = true;
      try {
        const params = { function: 'enrolFpClient', ...this.enrol };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.enrolModal = false; await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openDetail(c) { await this.openDetailById(c.fp_id); },
    async openDetailById(fpId) {
      try {
        const r = await executeJson({ function: 'getFpClientDetail', fp_id: fpId }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.detail = { client: d.Client, visits: d.Visits || [], methodChanges: d.MethodChanges || [] };
          this.tab = 'detail';
        }
      } catch (e) { console.error(e); }
    },

    openVisitModal(type) {
      this.visit = this.blankVisit();
      if (type) this.visit.visit_type = type;
      // Pre-fill method from current
      if (this.detail.client && this.detail.client.current_method_id) this.visit.method_id = this.detail.client.current_method_id;
      this.visitModal = true;
    },
    async saveVisit() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordFpVisit', fp_id: this.detail.client.fp_id, ...this.visit }, hms);
        if (r.data && r.data.success) { this.visitModal = false; await this.openDetailById(this.detail.client.fp_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openSwitchModal() {
      this.switchForm = { to_method_id: null, reason: 'side_effects', changed_at: new Date().toISOString().substring(0,10), notes: '' };
      this.switchModal = true;
    },
    async saveSwitch() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'changeFpMethod', fp_id: this.detail.client.fp_id, ...this.switchForm }, hms);
        if (r.data && r.data.success) { this.switchModal = false; await this.openDetailById(this.detail.client.fp_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadDefaulters() {
      try {
        const r = await executeJson({ function: 'getFpDefaulters' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.defaulters = d.Defaulters || [];
          this.defaulterStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadIndicators() {
      try {
        const r = await executeJson({ function: 'getFpIndicators', period: this.indicatorPeriod }, hms);
        if (r.data && r.data.success) this.indicators = r.data.data || {};
      } catch (e) { console.error(e); }
    },

    // ===== formatting =====
    rowClass(c) {
      if (c.days_to_appointment != null && c.days_to_appointment < -7) return 'row-warn';
      return '';
    },
    catPill(c) {
      return ({ LARC: 'hms-pill-success', hormonal_inj: 'hms-pill-info', hormonal_oral: 'hms-pill-info',
                barrier: 'hms-pill-muted', permanent: 'hms-pill-warn', natural: 'hms-pill-muted', emergency: 'hms-pill-danger' })[c] || 'hms-pill-muted';
    },
    catColor(c) {
      return ({ LARC: '#16a34a', hormonal_inj: '#2b6cb0', hormonal_oral: '#0891b2',
                barrier: '#94a3b8', permanent: '#d97706', natural: '#64748b', emergency: '#e53e3e' })[c] || '#94a3b8';
    },
    statusPill(s) {
      return ({ active: 'hms-pill-success', defaulter: 'hms-pill-warn', exited: 'hms-pill-muted',
                desire_pregnancy: 'hms-pill-info', completed_family: 'hms-pill-info',
                lost: 'hms-pill-danger', transferred: 'hms-pill-muted' })[s] || '';
    },
    visitTypePill(t) {
      return ({ initiation: 'hms-pill-success', refill: 'hms-pill-info', switch: 'hms-pill-warn',
                removal: 'hms-pill-danger', review: 'hms-pill-muted', side_effect: 'hms-pill-warn',
                complaint: 'hms-pill-warn', transfer_in: 'hms-pill-info' })[t] || '';
    },
    formatReason(r) {
      return ({ side_effects: 'Side effects', desire_pregnancy: 'Desire pregnancy',
                medical_contraindication: 'Medical contraindication', partner_objection: 'Partner objection',
                method_failure: 'Method failure', expired: 'Expired', simplification: 'Switch to simpler',
                transferred: 'Transferred out', other: 'Other' })[r] || r;
    },
    formatPeriod(p) { return ({ this_month: 'month', this_quarter: 'quarter', this_year: 'year' })[p] || 'period'; },
    mixPct(n) {
      const total = this.totalActiveClients;
      return total > 0 ? Math.round((parseInt(n || 0) / total) * 100) : 0;
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; flex-wrap: wrap; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; margin-left: 4px; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #38a169; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 8px 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-cyp-banner { padding: 16px; background: #f0fff4; border-radius: 8px; text-align: center; }
.hms-cyp-num { font-size: 36px; font-weight: 800; color: #276749; }

.hms-mix-row { display: grid; grid-template-columns: 280px 1fr 100px; gap: 12px; align-items: center; padding: 8px 0; border-bottom: 1px solid #edf2f7; }
.hms-mix-bar { background: #edf2f7; border-radius: 4px; height: 14px; overflow: hidden; }
.hms-mix-fill { height: 100%; }
.hms-mix-num  { text-align: right; }
.hms-mix-num strong { color: #1a202c; font-size: 16px; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.hms-ind-row strong { color: #2d3748; font-size: 16px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.text-warning { color: #d69e2e; }
.st-bad { color: #c53030; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
